import React, { useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { trainerApi } from '../services/api';

// Module-level cache for agent mapping
let agentMappingCache: Record<string, string> | null = null;
let fetchingPromise: Promise<Record<string, string>> | null = null;

const fetchAgentMapping = async (): Promise<Record<string, string>> => {
  if (agentMappingCache) return agentMappingCache;
  if (fetchingPromise) return fetchingPromise;
  
  fetchingPromise = trainerApi.getAgents().then((results: any[]) => {
    // console.log('Raw agent results:', results);
    const mapping: Record<string, string> = {};
    
    results.forEach((item: any) => {
      const agent = item.synth_agent || {};
      const synthId = agent.synth_agent_id;
      const retellId = agent.retell_agent_id;
      const label = item.synth_display_name || agent.synth_name || 'Agent';
      const note = item.note || agent.note || '';
      const fullLabel = note ? `${label} (${note})` : label;

      // console.log('Processing agent:', {
      //   synthId,
      //   synthIdType: typeof synthId,
      //   retellId,
      //   retellIdType: typeof retellId,
      //   fullLabel
      // });

      // Store both IDs in the mapping
      if (synthId !== null && synthId !== undefined) {
        const synthKey = String(synthId);
        mapping[synthKey] = fullLabel;
        // console.log(`Mapped synth ID: ${synthKey} (${typeof synthKey}) -> ${fullLabel}`);
      }

      if (retellId !== null && retellId !== undefined) {
        const retellKey = String(retellId);
        mapping[retellKey] = fullLabel;
        // console.log(`Mapped retell ID: ${retellKey} (${typeof retellKey}) -> ${fullLabel}`);
      }
    });
    
    // console.log('Final mapping keys:', Object.keys(mapping));
    // console.log('Final mapping entries:', Object.entries(mapping));
    agentMappingCache = mapping;
    fetchingPromise = null;
    return mapping;
  }).catch((err) => {
    console.error('Error fetching agents:', err);
    fetchingPromise = null;
    throw err;
  });
  return fetchingPromise;
};

interface AgentLabelProps {
  agentId: number | string | null | undefined;
}

const AgentLabel: React.FC<AgentLabelProps> = ({ agentId }) => {
  const [label, setLabel] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;
    const resolveLabel = async () => {
      if (!agentId) {
        if (isMounted) setLabel('');
        return;
      }
      setLoading(true);
      try {
        // console.log('Resolving label for agentId:', {
        //   value: agentId,
        //   type: typeof agentId,
        //   stringified: String(agentId)
        // });
        
        const mapping = await fetchAgentMapping();
        const key = String(agentId);
        
        // console.log('Lookup attempt:', {
        //   key,
        //   keyType: typeof key,
        //   availableKeys: Object.keys(mapping),
        //   hasKey: key in mapping,
        //   directLookup: mapping[key]
        // });
        
        if (isMounted) {
          const resolvedLabel = mapping[key] || String(agentId);
          // console.log('Resolution result:', {
          //   key,
          //   resolvedLabel,
          //   wasFound: mapping[key] !== undefined
          // });
          setLabel(resolvedLabel);
        }
      } catch (error) {
        console.error('Error resolving label:', error);
        if (isMounted) {
          setLabel(String(agentId));
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    resolveLabel();
    return () => { isMounted = false; };
  }, [agentId]);

  if (loading) return <CircularProgress size={16} />;
  return <span>{label}</span>;
};

export default AgentLabel; 